import { PropsWithChildren, forwardRef, useImperativeHandle } from 'react';
import { IonCheckbox, IonInput, IonItem, IonItemGroup, IonList } from '@ionic/react';
import { Controller, useForm } from 'react-hook-form';
import PhoneNumberInput from '../../../../components-library/PhoneNumberInput';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../../../../store/types';
import { REDUX_STORE_SLICES } from '../../../../services/exports/Constants';
import { SignupSchema, signupSchema } from './schema';
import { zodResolver } from '@hookform/resolvers/zod';
import useTheme from '../../../../hooks/ui/useTheme';
import classnames from 'classnames';
import { mailableLanguage } from '../../../../services/localization/Languages';

type SignupFormProps = PropsWithChildren & {
  onSubmit: (data: SignupSchema) => void;
  defaultValues?: SignupSchema;
};

export interface SignupFormHandle {
  submit: () => void;
}

const SignupForm = forwardRef<SignupFormHandle, SignupFormProps>(({ defaultValues, onSubmit }, ref) => {
  const companyId = useSelector((state: StoreInterface) => state[REDUX_STORE_SLICES.INITIAL_DATA]?.company?.id);
  const { i18n, t } = useTranslation(null, { keyPrefix: 'Modules:Mobile:Modals:SignUp:SignupForm' });
  const mergedDefaultValues: SignupSchema = {
    first_name: defaultValues?.first_name ?? '',
    last_name: defaultValues?.last_name ?? '',
    email: defaultValues?.email ?? '',
    phone_number: defaultValues?.phone_number ?? '',
    is_marketing_subscriber: defaultValues?.is_marketing_subscriber ?? false,
    offers_phone: false,
    // `?? undefined` rather than null: signupSchema types lang as an optional string,
    // and omitting it lets the backend fall back to the restaurant's legal language.
    lang: defaultValues?.lang ?? mailableLanguage(i18n.language) ?? undefined,
    company_id: defaultValues?.company_id ?? companyId,
  };
  const form = useForm<SignupSchema>({
    defaultValues: mergedDefaultValues,
    resolver: zodResolver(signupSchema),
  });
  const theme = useTheme();

  useImperativeHandle(
    ref,
    () => ({
      submit: form.handleSubmit(onSubmit),
    }),
    [form.handleSubmit],
  );

  return (
    <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
      <IonList lines="none" className="space-y-3">
        <IonItemGroup className="flex">
          <IonItem className="flex-1">
            <Controller
              name="first_name"
              control={form.control}
              render={({ field, fieldState }) => (
                <div>
                  <IonInput
                    className={classnames('bordered !px-4', {
                      'border-danger border-xs': !!fieldState.error?.message,
                    })}
                    style={{ '--placeholder-color': theme.color.mobile.tertiary }}
                    placeholder={t('placeholders.first_name')}
                    onIonChange={field.onChange}
                    {...field}
                  />
                  <p className="text-danger text-xs">{fieldState.error?.message}</p>
                </div>
              )}
            />
          </IonItem>
          <IonItem className="flex-1 [&::part(native)]:pl-0">
            <Controller
              name="last_name"
              control={form.control}
              render={({ field, fieldState }) => (
                <div>
                  <IonInput
                    className={classnames('bordered !px-4', {
                      'border-danger border-xs': !!fieldState.error?.message,
                    })}
                    style={{ '--placeholder-color': theme.color.mobile.tertiary }}
                    placeholder={t('placeholders.last_name')}
                    onIonChange={field.onChange}
                    {...field}
                  />
                  <p className="text-danger text-xs">{fieldState.error?.message}</p>
                </div>
              )}
            />
          </IonItem>
        </IonItemGroup>
        <IonItem className="overflow-visible">
          <Controller
            name="email"
            control={form.control}
            render={({ field, fieldState }) => (
              <div className="w-full">
                <IonInput
                  type="email"
                  className={classnames('bordered !px-4', { 'border-danger border-xs': !!fieldState.error?.message })}
                  style={{ '--placeholder-color': theme.color.mobile.tertiary }}
                  placeholder={t('placeholders.email')}
                  onIonChange={field.onChange}
                  {...field}
                />
                <p className="text-danger text-xs">{fieldState.error?.message}</p>
              </div>
            )}
          />
        </IonItem>
        <IonItem>
          <Controller
            name="phone_number"
            control={form.control}
            render={({ field: { ref: _, ...rest }, fieldState }) => (
              <PhoneNumberInput
                inputContainerStyle="border-xs bordered !px-4 !h-[46px]"
                placeholder={t('placeholders.mobile_number')}
                validationErrorClassName="text-danger text-xs"
                {...rest}
                validationErrors={fieldState?.error?.message ? [fieldState.error?.message] : []}
              />
            )}
          />
        </IonItem>
      </IonList>
      <IonList lines="none" className="space-y-4">
        <IonItem
          className="[&::part(native)]:h-fit"
          style={{
            '--min-height': 'auto',
            '--inner-padding-top': '0px',
            '--inner-padding-bottom': '0px',
          }}
          onClick={() => form.setValue('is_marketing_subscriber', !form.getValues('is_marketing_subscriber'))}
        >
          <Controller
            name="is_marketing_subscriber"
            control={form.control}
            render={({ field }) => (
              <IonCheckbox
                checked={field.value ?? false}
                className="[&::part(container)]:rounded [&::part(container)]:border-mobile-tertiary [&::part(container)]:border-xs mr-sm"
                style={{
                  '--checkbox-background-checked': theme.color.brand.primary,
                  '--size': '32px',
                  // Inside an ion-item the checkbox stretches and reserves an (empty) label slot,
                  // pushing the external label ~19px too far. Pin the host to the box size so only
                  // the mr-sm gap remains between the box and the label.
                  width: '32px',
                  minWidth: '32px',
                  flex: 'none',
                }}
              />
            )}
          />
          <p className="text-xs text-muted-foreground font-medium">{t('labels.exclusive_offers_via_email')}</p>
        </IonItem>
      </IonList>
    </form>
  );
});

export default SignupForm;
